(() => {
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const slugify = (value) => {
    const slug = value
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return slug || 'section';
  };

  const writeToClipboard = async (value) => {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(value);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    let copied = false;

    try {
      copied = document.execCommand('copy');
    } finally {
      textarea.remove();
    }

    if (!copied) {
      throw new Error('Clipboard copy was not available');
    }
  };

  const initGuideArticle = (root) => {
    if (!root || root.dataset.guideInitialized === 'true') return;
    root.dataset.guideInitialized = 'true';

    if (root.dataset.guidePreviewTitle) {
      document.title = root.dataset.guidePreviewTitle;
    }

    const content = root.querySelector('[data-guide-content]');
    const progress = root.querySelector('[data-guide-progress]');
    const backToTop = root.querySelector('[data-guide-back-to-top]');
    const tocRoots = Array.from(root.querySelectorAll('[data-guide-toc]'));
    const tocLists = Array.from(root.querySelectorAll('[data-guide-toc-list]'));
    const tocCounts = Array.from(root.querySelectorAll('[data-guide-toc-count]'));
    const headings = content ? Array.from(content.querySelectorAll('h2')) : [];
    const usedIds = new Set();

    headings.forEach((heading) => {
      const requestedId = heading.id || slugify(heading.textContent.trim());
      let uniqueId = requestedId;
      let suffix = 2;

      while (usedIds.has(uniqueId) || (document.getElementById(uniqueId) && document.getElementById(uniqueId) !== heading)) {
        uniqueId = `${requestedId}-${suffix}`;
        suffix += 1;
      }

      heading.id = uniqueId;
      usedIds.add(uniqueId);
    });

    if (!headings.length) {
      tocRoots.forEach((toc) => {
        toc.hidden = true;
      });
    } else {
      tocLists.forEach((list) => {
        const fragment = document.createDocumentFragment();

        headings.forEach((heading) => {
          const item = document.createElement('li');
          const link = document.createElement('a');
          link.href = `#${heading.id}`;
          link.textContent = heading.textContent.trim();
          link.dataset.guideTocLink = '';
          item.appendChild(link);
          fragment.appendChild(item);
        });

        list.replaceChildren(fragment);
      });

      tocCounts.forEach((count) => {
        count.textContent = `${headings.length} sections`;
      });
    }

    const tocLinks = Array.from(root.querySelectorAll('[data-guide-toc-link]'));

    const setActiveHeading = (id) => {
      tocLinks.forEach((link) => {
        const isActive = decodeURIComponent(link.hash.slice(1)) === id;
        link.classList.toggle('is-active', isActive);

        if (isActive) {
          link.setAttribute('aria-current', 'location');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    };

    tocLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        const id = decodeURIComponent(link.hash.slice(1));
        const target = document.getElementById(id);
        if (!target) return;

        event.preventDefault();
        const mobileToc = link.closest('details');
        const shouldCloseMobileToc = mobileToc && window.matchMedia('(max-width: 989px)').matches;

        if (shouldCloseMobileToc) {
          mobileToc.open = false;
        }

        const scrollToTarget = () => {
          target.scrollIntoView({
            behavior: reducedMotion() ? 'auto' : 'smooth',
            block: 'start'
          });
        };

        if (shouldCloseMobileToc) {
          window.requestAnimationFrame(scrollToTarget);
        } else {
          scrollToTarget();
        }

        setActiveHeading(id);

        if (window.history && window.history.pushState) {
          window.history.pushState(null, '', `#${id}`);
        }
      });
    });

    if ('IntersectionObserver' in window && headings.length) {
      const headingObserver = new IntersectionObserver((entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible.length) {
          setActiveHeading(visible[0].target.id);
        }
      }, {
        rootMargin: '-22% 0px -68% 0px',
        threshold: 0
      });

      headings.forEach((heading) => headingObserver.observe(heading));
    }

    const mobileToc = root.querySelector('.guide-toc-mobile');
    if (mobileToc && window.matchMedia('(max-width: 989px)').matches) {
      mobileToc.open = false;
    }

    const initialHash = decodeURIComponent(window.location.hash.slice(1));
    if (initialHash) {
      const initialTarget = document.getElementById(initialHash);
      if (initialTarget && root.contains(initialTarget)) {
        setActiveHeading(initialTarget.id);
        window.requestAnimationFrame(() => {
          initialTarget.scrollIntoView({ behavior: 'auto', block: 'start' });
        });
      }
    } else if (headings.length) {
      setActiveHeading(headings[0].id);
    }

    let ticking = false;
    const updateProgress = () => {
      ticking = false;
      if (!content || !progress) return;

      const rect = content.getBoundingClientRect();
      const pageTop = window.scrollY + rect.top;
      const start = pageTop - window.innerHeight * 0.28;
      const end = pageTop + content.offsetHeight - window.innerHeight * 0.58;
      const distance = Math.max(end - start, 1);
      const ratio = Math.min(Math.max((window.scrollY - start) / distance, 0), 1);

      progress.style.setProperty('--guide-progress', `${ratio * 100}%`);

      if (backToTop) {
        backToTop.hidden = ratio < 0.12;
      }
    };

    const requestProgressUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateProgress);
    };

    window.addEventListener('scroll', requestProgressUpdate, { passive: true });
    window.addEventListener('resize', requestProgressUpdate);
    updateProgress();

    root.querySelectorAll('[data-guide-copy]').forEach((button) => {
      button.addEventListener('click', async () => {
        const status = root.querySelector('[data-guide-copy-status]');
        const url = button.dataset.copyUrl || window.location.href;
        const originalLabel = button.getAttribute('aria-label');

        try {
          await writeToClipboard(url);
          if (status) status.textContent = 'Link copied';
          button.setAttribute('aria-label', 'Link copied');
        } catch (error) {
          if (status) status.textContent = 'Copy failed — use your browser address bar';
        }

        window.setTimeout(() => {
          if (status) status.textContent = '';
          if (originalLabel) button.setAttribute('aria-label', originalLabel);
        }, 2400);
      });
    });

    root.querySelectorAll('[data-guide-share]').forEach((button) => {
      button.addEventListener('click', async () => {
        const originalLabel = button.getAttribute('aria-label');
        const shareData = {
          title: button.dataset.shareTitle || document.title,
          url: button.dataset.shareUrl || window.location.href
        };

        if (navigator.share) {
          try {
            await navigator.share(shareData);
            return;
          } catch (error) {
            if (error && error.name === 'AbortError') return;
          }
        }

        try {
          await writeToClipboard(shareData.url);
          button.setAttribute('aria-label', 'Link copied');
          window.setTimeout(() => {
            if (originalLabel) button.setAttribute('aria-label', originalLabel);
          }, 2400);
        } catch (error) {
          window.location.href = `mailto:?subject=${encodeURIComponent(shareData.title)}&body=${encodeURIComponent(shareData.url)}`;
        }
      });
    });

    if (backToTop) {
      backToTop.addEventListener('click', () => {
        root.scrollIntoView({
          behavior: reducedMotion() ? 'auto' : 'smooth',
          block: 'start'
        });
      });
    }
  };

  const initAll = (scope = document) => {
    if (scope.matches && scope.matches('[data-guide-article]')) {
      initGuideArticle(scope);
    }

    scope.querySelectorAll('[data-guide-article]').forEach(initGuideArticle);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();
